import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter as FilterIcon, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Headers we expose as quick-filter dropdowns (case-insensitive substring match)
const FILTER_HINTS = {
  location: ['location', 'city', 'emirate', 'area', 'region'],
  clinic: ['clinic', 'centre', 'center', 'medical centre', 'medical center'],
  hospital: ['hospital'],
};

const SpreadsheetViewer = ({ fileId, filename }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/api/insurance/global/file/${fileId}/data`)
      .then(r => { if (!cancel) setData(r.data); })
      .catch(e => { if (!cancel) setError(e?.response?.data?.detail || 'Failed to load'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [fileId]);

  // Identify which header indices match each filter hint
  const filterColumns = useMemo(() => {
    if (!data?.headers) return {};
    const out = {};
    Object.entries(FILTER_HINTS).forEach(([key, needles]) => {
      const idx = data.headers.findIndex(h => {
        const low = (h || '').toLowerCase();
        return needles.some(n => low.includes(n));
      });
      if (idx !== -1) out[key] = idx;
    });
    return out;
  }, [data]);

  // Unique values per filter column
  const uniqueValues = useMemo(() => {
    if (!data?.rows) return {};
    const out = {};
    Object.entries(filterColumns).forEach(([key, idx]) => {
      const set = new Set();
      data.rows.forEach(r => {
        const v = (r[idx] || '').trim();
        if (v) set.add(v);
      });
      out[key] = Array.from(set).sort((a, b) => a.localeCompare(b));
    });
    return out;
  }, [data, filterColumns]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toLowerCase();
    return data.rows.filter(row => {
      // Quick filters
      for (const [key, idx] of Object.entries(filterColumns)) {
        const want = filters[key];
        if (want && (row[idx] || '').toLowerCase() !== want.toLowerCase()) return false;
      }
      // Text search across all cells
      if (q) {
        return row.some(cell => (cell || '').toLowerCase().includes(q));
      }
      return true;
    });
  }, [data, filters, filterColumns, search]);

  const clearAll = () => { setFilters({}); setSearch(''); };

  if (loading) return <div className="py-6 text-center text-xs text-gray-500" data-testid={`xls-loading-${fileId}`}>Rendering spreadsheet…</div>;
  if (error) return <div className="py-3 text-xs text-red-500">{error}</div>;
  if (!data || !data.headers?.length) return <div className="py-3 text-xs text-gray-500">No data found in {filename}</div>;

  const hasFilters = Object.keys(filterColumns).length > 0;
  const activeCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  return (
    <div className="space-y-2" data-testid={`xls-viewer-${fileId}`}>
      {/* Search + filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search anywhere…"
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-emerald-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
            data-testid={`xls-search-${fileId}`}
          />
        </div>
        {Object.entries(filterColumns).map(([key, idx]) => (
          <select
            key={key}
            value={filters[key] || ''}
            onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
            className="px-2 py-1.5 text-xs rounded-md border border-emerald-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200 max-w-[160px]"
            data-testid={`xls-filter-${key}-${fileId}`}
          >
            <option value="">{data.headers[idx] || key}: All</option>
            {uniqueValues[key]?.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        ))}
        {activeCount > 0 && (
          <button onClick={clearAll} className="text-xs text-rose-600 flex items-center gap-1 px-2 py-1.5" data-testid={`xls-clear-${fileId}`}>
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {!hasFilters && (
        <p className="text-[10px] text-gray-500 flex items-center gap-1">
          <FilterIcon className="h-3 w-3" /> Tip: Add columns named "Location", "Clinic" or "Hospital" in your sheet to enable quick filters.
        </p>
      )}

      <div className="border rounded-md overflow-hidden">
        <div className="max-h-80 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-emerald-100 sticky top-0">
              <tr>
                {data.headers.map((h, i) => (
                  <th key={i} className="text-left px-2 py-1.5 font-semibold text-emerald-900 whitespace-nowrap">{h || `Col ${i + 1}`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan={data.headers.length} className="px-2 py-4 text-center text-gray-500">No matching rows</td></tr>
              ) : filteredRows.map((row, ri) => (
                <tr key={ri} className="border-t hover:bg-emerald-50/40">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-2 py-1.5 text-gray-700 whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[10px] text-gray-500">
        Showing {filteredRows.length} of {data.row_count} rows
      </p>
    </div>
  );
};

export default SpreadsheetViewer;
